import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { InternalAuditResultsByParentIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditResultsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit results data to match the GraphQL query structure
 */
const mapTrpcInternalAuditResultsToGraphQL = (
  trpcData: InternalAuditResultsByParentIdResponse
): GetInternalAuditResultsByParentIdQuery => {
  return {
    ...trpcData,
    document_internal_audit_result: trpcData.document_internal_audit_result.map(
      (result) => ({
        ...result,
        __typename: ParentTypes.DocumentInternalAuditResult,
      })
    ),
    obligation_internal_audit_result:
      trpcData.obligation_internal_audit_result.map((result) => ({
        ...result,
        __typename: ParentTypes.ObligationInternalAuditResult,
      })),
    risk_controlled_internal_audit_result:
      trpcData.risk_controlled_internal_audit_result.map((result) => ({
        ...result,
        __typename: ParentTypes.RiskControlledInternalAuditResult,
      })),
    risk_uncontrolled_internal_audit_result:
      trpcData.risk_uncontrolled_internal_audit_result.map((result) => ({
        ...result,
        __typename: ParentTypes.RiskUncontrolledInternalAuditResult,
      })),
    control_test_internal_audit_result:
      trpcData.control_test_internal_audit_result.map((result) => ({
        ...result,
        __typename: ParentTypes.ControlTestInternalAuditResult,
      })),
    impact_internal_audit_rating: trpcData.impact_internal_audit_rating.map(
      (rating) => ({ ...rating, ratedItem: rating.ratedItem! })
    ),
  };
};

export const useGetInternalAuditResultsByParentId = (
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditResult.internalAuditResultByParentId.queryOptions(
      {
        parentId,
      }
    ),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcInternalAuditResultsToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
